import { hashSeed, Rng } from "./rng.js";
import type { GameState, LevelDef, Pos } from "./types.js";

/**
 * Quiz ("Tahmin") mode: blast × general knowledge.
 *
 * The board is an 8x8 blast grid mapped 1:1 onto an 8x8 block grid over a pixel picture.
 * Clearing the tile at (r, c) permanently reveals picture block (r, c) — so a rocket reveals a
 * strip, a bomb a 3x3 patch, and *where* you blast is a real information decision.
 * The player may guess at any time from a small set of options; a wrong guess costs moves.
 * Score is the number of moves used (fewer is better), which is also the shareable number.
 *
 * Pure TS, deterministic (Rng), no DOM: the bot, the video factory and the client share it.
 */

/** A picture in a content pack (built by tools/art-factory/pictures.mjs). */
export interface Picture {
  id: string;
  /** Theme = leaderboard bucket and option pool ("flags", "animals", "food", ...). */
  theme: string;
  /** Finer pool for distractors (e.g. continent for flags). Falls back to theme. */
  group?: string;
  /** Square edge in pixels (32). */
  size: number;
  palette: string[];
  /** size*size chars: '.' = transparent, else palette index in PIXEL_ALPHABET. */
  pixels: string;
  /** Display label per language; also the answer text. */
  label: Record<string, string>;
  /** Accepted alternative answers (any language), e.g. "Turkey" for "Türkiye". */
  aliases?: string[];
  credit?: string;
}

export const PIXEL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

export function decodePixels(p: Picture): number[] {
  const out = new Array<number>(p.size * p.size);
  for (let i = 0; i < out.length; i++) {
    const ch = p.pixels[i] ?? ".";
    out[i] = ch === "." ? -1 : PIXEL_ALPHABET.indexOf(ch);
  }
  return out;
}

export function encodePixels(indices: number[]): string {
  let s = "";
  for (const i of indices) s += i < 0 ? "." : PIXEL_ALPHABET[i]!;
  return s;
}

export interface QuizRules {
  boardW: number;
  boardH: number;
  moves: number;
  colors: number;
  /** Moves lost per wrong guess. */
  wrongPenalty: number;
  optionCount: number;
}

export const QUIZ_RULES: QuizRules = { boardW: 8, boardH: 8, moves: 20, colors: 5, wrongPenalty: 2, optionCount: 4 };

/** The blast level backing a quiz round. Goals never complete; the round ends by guess or by moves. */
export function quizLevel(picture: Picture, seed: number, rules: QuizRules = QUIZ_RULES): LevelDef {
  return {
    id: `quiz:${picture.id}:${seed >>> 0}`,
    seed: seed >>> 0,
    width: rules.boardW,
    height: rules.boardH,
    colors: rules.colors,
    moves: rules.moves,
    goals: [{ type: "clear", count: Number.MAX_SAFE_INTEGER }],
    imageId: picture.id,
  };
}

export interface QuizState {
  picture: Picture;
  rules: QuizRules;
  /** boardW*boardH, row-major: block revealed? */
  revealed: boolean[];
  /** Option picture ids (answer included, shuffled). */
  options: string[];
  optionLabels: Record<string, string>[];
  eliminated: boolean[];
  /** Typed answers that were wrong (shown back to the player). */
  wrongLabels: string[];
  wrong: number;
  solved: boolean;
  finished: boolean;
  /** Moves spent (including penalties) at the moment the round finished. */
  movesUsed: number;
}

/**
 * Build a round: answer + (optionCount-1) distractors from the same group, then theme, then any.
 * Distractor choice is seeded so everyone playing the same picture+seed sees the same options.
 */
export function createQuiz(picture: Picture, pool: Picture[], seed: number, rules: QuizRules = QUIZ_RULES): QuizState {
  const rng = new Rng((seed ^ 0x51a7e) >>> 0);
  const others = pool.filter((p) => p.id !== picture.id);
  const tiers = [
    others.filter((p) => (p.group ?? p.theme) === (picture.group ?? picture.theme)),
    others.filter((p) => p.theme === picture.theme),
    others,
  ];
  const chosen: Picture[] = [];
  const seen = new Set<string>([picture.id]);
  for (const tier of tiers) {
    const cands = tier.filter((p) => !seen.has(p.id));
    while (chosen.length < rules.optionCount - 1 && cands.length) {
      const i = rng.int(cands.length);
      const p = cands.splice(i, 1)[0]!;
      seen.add(p.id);
      chosen.push(p);
    }
    if (chosen.length >= rules.optionCount - 1) break;
  }
  const all = [picture, ...chosen];
  // Fisher–Yates with the seeded rng.
  for (let i = all.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [all[i], all[j]] = [all[j]!, all[i]!];
  }
  return {
    picture,
    rules,
    revealed: new Array<boolean>(rules.boardW * rules.boardH).fill(false),
    options: all.map((p) => p.id),
    optionLabels: all.map((p) => p.label),
    eliminated: all.map(() => false),
    wrongLabels: [],
    wrong: 0,
    solved: false,
    finished: false,
    movesUsed: 0,
  };
}

/** Mark blocks revealed for the cells cleared by a move (pre-gravity positions). */
export function revealBlocks(q: QuizState, cleared: Pos[]): number {
  let added = 0;
  for (const p of cleared) {
    const i = p.r * q.rules.boardW + p.c;
    if (i < 0 || i >= q.revealed.length || q.revealed[i]) continue;
    q.revealed[i] = true;
    added++;
  }
  return added;
}

export function revealedFraction(q: QuizState): number {
  let n = 0;
  for (const r of q.revealed) if (r) n++;
  return n / q.revealed.length;
}

/** Is picture pixel (x, y) inside a revealed block? */
export function pixelRevealed(q: QuizState, x: number, y: number): boolean {
  const bw = q.picture.size / q.rules.boardW;
  const bh = q.picture.size / q.rules.boardH;
  const c = Math.min(q.rules.boardW - 1, Math.floor(x / bw));
  const r = Math.min(q.rules.boardH - 1, Math.floor(y / bh));
  return q.revealed[r * q.rules.boardW + c] === true;
}

export interface GuessResult {
  correct: boolean;
  /** Moves deducted (0 when correct). */
  penalty: number;
  finished: boolean;
}

/**
 * Guess option `index`. Correct => round solved. Wrong => option eliminated, moves deducted; if
 * moves run out or only the answer remains, the round is over (unsolved).
 */
export function guess(q: QuizState, game: GameState, index: number): GuessResult {
  if (q.finished || index < 0 || index >= q.options.length || q.eliminated[index]) {
    return { correct: false, penalty: 0, finished: q.finished };
  }
  const movesUsedNow = () => game.level.moves - game.movesLeft;
  if (q.options[index] === q.picture.id) {
    q.solved = true;
    q.finished = true;
    q.movesUsed = movesUsedNow();
    game.status = "won";
    return { correct: true, penalty: 0, finished: true };
  }
  q.eliminated[index] = true;
  q.wrong++;
  const penalty = Math.min(q.rules.wrongPenalty, game.movesLeft);
  game.movesLeft -= penalty;
  const remaining = q.eliminated.filter((e) => !e).length;
  if (game.movesLeft <= 0 || remaining <= 1) {
    q.finished = true;
    q.movesUsed = movesUsedNow();
    game.status = "lost";
  }
  return { correct: false, penalty, finished: q.finished };
}

/**
 * Normalize a free-text answer for comparison: Turkish-aware lowercase, diacritics folded,
 * punctuation/whitespace dropped. "Türkiye" == "turkiye" == "TÜRKİYE".
 */
export function normalizeAnswer(s: string): string {
  return s
    .replace(/İ/g, "i")
    .replace(/I/g, "ı")
    .toLowerCase()
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

export function matchesAnswer(picture: Picture, text: string): boolean {
  const n = normalizeAnswer(text);
  if (!n) return false;
  const cands = [...Object.values(picture.label), ...(picture.aliases ?? [])];
  return cands.some((c) => normalizeAnswer(c) === n);
}

export interface Suggestion {
  id: string;
  label: string;
}

/**
 * Type-ahead over a pool's labels in `lang`: prefix matches first, then substring, deduped.
 * The client offers these as tappable answers so a guess is exact, not fuzzy — and never lucky:
 * with hundreds of labels there is no 1-in-4 shot.
 */
export function suggestAnswers(pool: Picture[], text: string, lang: string, limit = 5): Suggestion[] {
  const n = normalizeAnswer(text);
  if (n.length < 1) return [];
  const seen = new Set<string>();
  const prefix: Suggestion[] = [];
  const inner: Suggestion[] = [];
  for (const p of pool) {
    const label = p.label[lang] ?? p.label.en ?? p.id;
    const ln = normalizeAnswer(label);
    if (seen.has(ln)) continue;
    if (ln.startsWith(n)) {
      seen.add(ln);
      prefix.push({ id: p.id, label });
    } else if (ln.includes(n)) {
      seen.add(ln);
      inner.push({ id: p.id, label });
    }
  }
  const byLen = (a: Suggestion, b: Suggestion) => a.label.length - b.label.length || a.label.localeCompare(b.label, lang);
  return [...prefix.sort(byLen), ...inner.sort(byLen)].slice(0, limit);
}

/**
 * Free-text guess. Correct => solved. Wrong => penalty moves; the round ends unsolved only when
 * moves run out. Repeating a wrong answer costs nothing (it is just echoed back).
 */
export function guessText(q: QuizState, game: GameState, text: string): GuessResult {
  if (q.finished) return { correct: false, penalty: 0, finished: true };
  const movesUsedNow = () => game.level.moves - game.movesLeft;
  if (matchesAnswer(q.picture, text)) {
    q.solved = true;
    q.finished = true;
    q.movesUsed = movesUsedNow();
    game.status = "won";
    return { correct: true, penalty: 0, finished: true };
  }
  const n = normalizeAnswer(text);
  if (!n || q.wrongLabels.some((w) => normalizeAnswer(w) === n)) return { correct: false, penalty: 0, finished: false };
  q.wrongLabels.push(text.trim());
  q.wrong++;
  const penalty = Math.min(q.rules.wrongPenalty, game.movesLeft);
  game.movesLeft -= penalty;
  if (game.movesLeft <= 0) {
    q.finished = true;
    q.movesUsed = movesUsedNow();
    game.status = "lost";
  }
  return { correct: false, penalty, finished: q.finished };
}

/** Out of moves without a guess: the round ends unsolved (the client then shows the answer). */
export function endQuizByMoves(q: QuizState, game: GameState): void {
  if (q.finished) return;
  q.finished = true;
  q.movesUsed = game.level.moves - game.movesLeft;
}

/**
 * Points for the round. Legible and skill-only: 10 per unused move, minus 5 per wrong guess,
 * never below 10 for a solve. Unsolved = 0. The leaderboard sorts by movesUsed, not points.
 */
export function quizScore(q: QuizState, level: LevelDef): number {
  if (!q.solved) return 0;
  return Math.max(10, (level.moves - q.movesUsed) * 10 - q.wrong * 5);
}

/** Wordle-style share grid: revealed blocks as filled squares, one row per board row. */
export function shareGrid(q: QuizState, filled = "🟨", empty = "⬛"): string {
  const rows: string[] = [];
  for (let r = 0; r < q.rules.boardH; r++) {
    let s = "";
    for (let c = 0; c < q.rules.boardW; c++) s += q.revealed[r * q.rules.boardW + c] ? filled : empty;
    rows.push(s);
  }
  return rows.join("\n");
}
